import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import BulkImport from "@/components/BulkImport";
import { Card, PageHeader, Modal, Button, Field, Input, Textarea, Select } from "@/components/ui-kit";
import BatchingNamePicker from "@/components/BatchingNamePicker";
import { Package, Plus, Trash2, Pencil, Eye, Lock, Printer } from "lucide-react";
import PdfViewerModal from "@/components/pdf/PdfViewerModal";
import { todayISO, parseQty, formatNumber } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { DatePicker } from "@/components/DatePicker";

/**
 * Iter v34b9 — Surat Jalan Batching MATERIAL (penjualan langsung batu/pasir/dsb).
 * Format mirip SJ Ready Mix, tetapi:
 *  - "Mutu Beton" → "Material" (dropdown master batching_materials)
 *  - "Slump" → 3 field: Panjang, Lebar, Tinggi (m) — auto-hitung Volume p×l×t
 *  - Kolom "Keterangan" dihapus
 *  - "Dispatcher" → "Pengirim"
 *  - Waktu berangkat / tiba / cor dihilangkan
 *  - Nomor SJ auto (SJ-MAT-YYMM-####) — configurable via doc_number_formats
 *  - Stok material auto-deduct via batching_stock_entries
 *
 * Iter v34bm (Task #3, #4) — "Proyek" → "Lokasi Proyek/Bongkar" (dropdown master).
 * PO/Pelanggan/Lokasi dropdown dari PO Rekap Kuota kategori material batching.
 * Auto-fill Pelanggan + Lokasi ketika PO dipilih. Input material manual
 * hanya bisa diakses user yang punya perm `batching.sj.manual_input`.
 */
const blankItem = { mode: "kubikasi", material_id: "", material_name: "", truck_id: "", panjang: 0, lebar: 0, tinggi: 0, volume_m3: 0, qty_kg: 0 };
const blankForm = {
  sj_number: "", date: todayISO(), pelanggan: "", proyek: "", no_po_kontrak: "",
  no_truk: "", sopir: "", items: [{ ...blankItem }],
  catatan_penting: "",
  name_penerima: "", name_sopir: "", name_pengirim: "",
};

export default function BatchingMaterialSj() {
  const { hasPerm } = useAuth();
  const canEditNumber = hasPerm("batching.sj.edit_number");
  const canCreate = hasPerm("batching.sj.create");
  const canEdit = hasPerm("batching.sj.edit");
  const canDelete = hasPerm("batching.sj.delete");
  // Iter v34bm — Perm khusus untuk isi manual (dulu semua orang bisa ketik bebas).
  const canManualInput = hasPerm("batching.sj.manual_input");
  // Iter v34ct (#3) — Cetak blanko SJ Material.
  const canPrintBlank = hasPerm("batching.sj.print_blank");
  const [blankOpen, setBlankOpen] = useState(false);
  const [blankCopies, setBlankCopies] = useState(2);
  const [blankPdfOpen, setBlankPdfOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [clients, setClients] = useState([]);
  // Iter v34bm — Master lokasi + PO Rekap Kuota (kategori material batching).
  const [locations, setLocations] = useState([]);
  const [rekapPos, setRekapPos] = useState([]);
  const [pdfViewerSj, setPdfViewerSj] = useState(null);

  const load = () => api.get("/batching/material-sj").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => {
    load();
    api.get("/batching/drivers").then((r) => setDrivers(r.data || [])).catch(() => {});
    api.get("/batching/trucks", { params: { truck_type: "material" } }).then((r) => setTrucks(r.data || [])).catch(() => {});
    api.get("/batching/materials").then((r) => setMaterials(r.data || [])).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    // Iter v34bm — Master locations
    api.get("/batching/locations").then((r) => setLocations(r.data || [])).catch(() => {});
    // Iter v34bm (Task #4) — PO Rekap Kuota kategori material/batching, hide yang sudah fully-billed.
    api.get("/pos").then((r) => {
      const cat = (p) => (p.category || "").toLowerCase();
      // Iter v34br (Task 10) — Sembunyikan PO yang di-tutup manual (is_closed).
      const all = (r.data || []).filter((p) => !p.is_closed && (cat(p).includes("material") || cat(p).includes("batching")));
      const notFullyBilled = all.filter((p) => {
        const t = Number(p.grand_total ?? p.total_value ?? p.po_total) || 0;
        const b = Number(p.billed_total ?? p.billed_amount) || 0;
        const cnt = Number(p.billed_count) || 0;
        const allowed = Number(p.billing_count_allowed) || 0;
        const byValue = t > 0 && b >= t - 0.01;
        const byCount = allowed > 0 && cnt >= allowed;
        return !(byValue || byCount);
      });
      setRekapPos(notFullyBilled);
    }).catch(() => {});
  }, []);

  // Auto-fill next SJ number when form opens (blank/create).
  useEffect(() => {
    if (open && !editId && !form.sj_number) {
      api.get("/batching/material-sj/next-number").then((r) => {
        if (r.data?.sj_number) setForm((f) => ({ ...f, sj_number: r.data.sj_number }));
      }).catch(() => {});
    }
  }, [open, editId]);

  const openCreate = () => { setForm({ ...blankForm, items: [{ ...blankItem }] }); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({
      ...blankForm,
      ...r,
      items: (r.items || []).map((it) => ({ ...blankItem, ...it })),
    });
    setEditId(r.id);
    setOpen(true);
  };

  const setItem = (i, k, v) => setForm((f) => {
    const items = f.items.slice();
    items[i] = { ...items[i], [k]: v };
    // Iter v34ba — Mode change: reset counters
    if (k === "mode") {
      if (v === "kilogram") {
        items[i].panjang = 0; items[i].lebar = 0; items[i].tinggi = 0; items[i].volume_m3 = 0;
      } else {
        items[i].qty_kg = 0;
      }
    }
    // Auto-fill P/L/T from truck if truck_id selected in kubikasi mode.
    // Iter v34be — Input dalam cm; master truck menyimpan meter → dikali 100.
    if (k === "truck_id" && v) {
      const tr = trucks.find((tt) => tt.id === v);
      if (tr) {
        if (tr.panjang_m) items[i].panjang = Number((tr.panjang_m * 100).toFixed(2));
        if (tr.lebar_m) items[i].lebar = Number((tr.lebar_m * 100).toFixed(2));
        if (tr.tinggi_m) items[i].tinggi = Number((tr.tinggi_m * 100).toFixed(2));
      }
    }
    // Iter v34be — Auto-compute volume = (p × l × t) / 1.000.000 (cm³ → m³), max 2 desimal.
    if ((items[i].mode || "kubikasi") === "kubikasi" && (k === "panjang" || k === "lebar" || k === "tinggi" || k === "truck_id")) {
      const p = parseQty(items[i].panjang);
      const l = parseQty(items[i].lebar);
      const t = parseQty(items[i].tinggi);
      const auto = (p * l * t) / 1000000;
      if (auto > 0) items[i].volume_m3 = Number(auto.toFixed(2));
      else items[i].volume_m3 = 0;
    }
    return { ...f, items };
  });

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...blankItem }] }));
  const rmItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, k) => k !== i) }));

  const save = async () => {
    if (!form.pelanggan) { toast.error("Pelanggan wajib diisi"); return; }
    const items = form.items
      .filter((i) => i.material_name && (
        (i.mode === "kilogram" && parseQty(i.qty_kg) > 0) ||
        ((i.mode !== "kilogram") && parseQty(i.volume_m3) > 0)
      ))
      .map((i) => ({
        mode: i.mode || "kubikasi",
        material_id: i.material_id || "",
        material_name: i.material_name,
        panjang: parseQty(i.panjang),
        lebar: parseQty(i.lebar),
        tinggi: parseQty(i.tinggi),
        volume_m3: parseQty(i.volume_m3),
        qty_kg: parseQty(i.qty_kg),
      }));
    if (!items.length) { toast.error("Minimal 1 item dengan volume/berat > 0"); return; }
    setSaving(true);
    try {
      const payload = { ...form, items };
      if (editId) {
        await api.patch(`/batching/material-sj/${editId}`, payload);
        toast.success("SJ Material diperbarui");
      } else {
        const r = await api.post("/batching/material-sj", payload);
        toast.success(`SJ Material tersimpan: ${r.data.sj_number}`);
      }
      setOpen(false); setForm(blankForm); setEditId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal simpan");
    } finally { setSaving(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Hapus SJ ${r.sj_number}? Stok material akan dikembalikan.`)) return;
    try { await api.delete(`/batching/material-sj/${r.id}`); toast.success("SJ dihapus"); load(); }
    catch (e) { toast.error("Gagal hapus"); }
  };

  return (
    <div className="min-h-full bg-paper p-4 md:p-6 lg:p-8">
      <div className="max-w-[1500px] mx-auto space-y-6">
        <PageHeader
          overline="Batching Plant · Penjualan Material"
          title="Surat Jalan Material"
          description="Penjualan langsung material (batu, pasir, dsb). Stok material auto-terpotong saat SJ dibuat."
          action={(canCreate || canPrintBlank) ? (
            <div className="flex items-center gap-2">
              {canCreate && <BulkImport kind="batching_sj_mat" onDone={load} />}
              {/* Iter v34ct (#3) — Cetak SJ Material kosongan (blanko) untuk ditulis tangan. */}
              {canPrintBlank && (
                <Button variant="secondary" onClick={() => setBlankOpen(true)} data-testid="material-sj-blank-open">
                  <Printer className="h-3.5 w-3.5"/> Cetak SJ Kosongan
                </Button>
              )}
              {canCreate && (
                <Button onClick={openCreate} data-testid="material-sj-open-create">
                  <Plus className="h-3.5 w-3.5"/> Buat SJ Material
                </Button>
              )}
            </div>
          ) : null}
        />

        {/* Iter v34bh (Task #1c) — Hint pengaturan cetak untuk Continuous Form printer. */}
        <div className="bg-blue-50 border border-blue-200 text-blue-900 text-xs px-3 py-2 flex items-start gap-2">
          <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-600 text-white text-[10px] font-bold shrink-0 mt-0.5">i</span>
          <div>
            <b>Pengaturan Cetak Surat Jalan Material</b> — Ukuran kanvas: <b>Continuous Form 9,5 × 5,5 inch (24,13 × 13,97 cm)</b>.
            Saat mencetak dari printer dot-matrix / continuous, atur <b>Paper Size = Custom 24.13 × 13.97 cm</b> dan
            <b> Orientation = Portrait</b> di driver printer (konten sudah otomatis landscape di dalam PDF).
          </div>
        </div>

        <Card className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="material-sj-table">
              <thead className="bg-stone-50">
                <tr className="text-left text-[11px] uppercase tracking-widest text-stone-500 border-b">
                  <th className="py-3 px-3">No. SJ</th>
                  <th className="py-3 px-3">Tanggal</th>
                  <th className="py-3 px-3">Pelanggan</th>
                  <th className="py-3 px-3">Lokasi Proyek/Bongkar</th>
                  <th className="py-3 px-3">Truk / Sopir</th>
                  <th className="py-3 px-3 text-right">Vol / Berat</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-10 text-stone-400">Belum ada SJ Material.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`material-sj-row-${r.id}`}>
                    <td className="py-2 px-3 font-mono">{r.sj_number}</td>
                    <td className="py-2 px-3">{r.date}</td>
                    <td className="py-2 px-3">{r.pelanggan}</td>
                    <td className="py-2 px-3 text-stone-600">{r.proyek || "-"}</td>
                    <td className="py-2 px-3 text-stone-600">{r.no_truk || "-"} / {r.sopir || "-"}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">
                      {/* Iter v34bf — Tampilkan volume dgn 2 desimal & berat dgn 3 desimal (tidak dibulatkan) */}
                      {r.total_volume_m3 > 0 && <span className="mr-2">{formatNumber(r.total_volume_m3, 2)} m³</span>}
                      {r.total_qty_kg > 0 && <span className="text-amber-700">{formatNumber(r.total_qty_kg, 3)} kg</span>}
                      {!r.total_volume_m3 && !r.total_qty_kg && <span className="text-stone-300">—</span>}
                    </td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      <button onClick={() => setPdfViewerSj(r)} className="text-stone-500 hover:text-blue-700 px-2" title="Lihat PDF" data-testid={`material-sj-pdf-${r.id}`}>
                        <Eye className="h-3.5 w-3.5"/>
                      </button>
                      {canEdit && <button onClick={() => openEdit(r)} className="text-stone-500 hover:text-terra-700 px-2" title="Edit" data-testid={`material-sj-edit-${r.id}`}><Pencil className="h-3.5 w-3.5"/></button>}
                      {canDelete && <button onClick={() => del(r)} className="text-stone-500 hover:text-red-700 px-2" title="Hapus" data-testid={`material-sj-del-${r.id}`}><Trash2 className="h-3.5 w-3.5"/></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Create/Edit Modal */}
      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit SJ Material" : "Buat SJ Material"} size="xl">
        <div className="space-y-4" data-testid="material-sj-modal">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="No. SJ">
              <div className="flex items-center gap-2">
                <Input
                  className="flex-1 font-mono"
                  value={form.sj_number}
                  onChange={(e) => canEditNumber && setForm({ ...form, sj_number: e.target.value })}
                  readOnly={!canEditNumber}
                  placeholder="SJ-MAT-2607-0001"
                  data-testid="material-sj-number"
                />
                {!canEditNumber && <Lock className="h-4 w-4 text-stone-400" title="Auto-generate"/>}
              </div>
            </Field>
            <Field label="Tanggal">
              <DatePicker value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}/>
            </Field>
            <Field label="No. PO / Kontrak" hint="Pilih dari Rekap PO & Kuota kategori material batching (yang belum habis kuota).">
              <Select
                value={form.no_po_kontrak}
                onChange={(e) => {
                  const val = e.target.value;
                  const po = rekapPos.find((p) => p.po_number === val);
                  setForm((f) => ({
                    ...f,
                    no_po_kontrak: val,
                    // Auto-fill Pelanggan + Lokasi dari PO saat PO dipilih.
                    pelanggan: po ? (po.client_name || f.pelanggan) : f.pelanggan,
                    proyek: po ? (po.project || po.delivery_location || f.proyek) : f.proyek,
                  }));
                }}
                data-testid="material-sj-po-kontrak"
              >
                <option value="">— Pilih PO —</option>
                {rekapPos.map((po) => (
                  <option key={po.id} value={po.po_number}>
                    {po.client_name ? `${po.po_number} — ${po.client_name}` : po.po_number}
                  </option>
                ))}
                {/* Fallback untuk edit SJ lama */}
                {form.no_po_kontrak && !rekapPos.some((po) => po.po_number === form.no_po_kontrak) && (
                  <option value={form.no_po_kontrak}>{form.no_po_kontrak} (lama)</option>
                )}
              </Select>
            </Field>
            <Field label="Pelanggan">
              <Select value={form.pelanggan} onChange={(e) => setForm({ ...form, pelanggan: e.target.value })} data-testid="material-sj-pelanggan">
                <option value="">— Pilih Pelanggan —</option>
                {clients.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                {form.pelanggan && !clients.some((c) => c.name === form.pelanggan) && (
                  <option value={form.pelanggan}>{form.pelanggan} (lama)</option>
                )}
              </Select>
            </Field>
            <Field label="Lokasi Proyek/Bongkar">
              <Select value={form.proyek} onChange={(e) => setForm({ ...form, proyek: e.target.value })} data-testid="material-sj-lokasi">
                <option value="">— Pilih Lokasi —</option>
                {locations.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
                {form.proyek && !locations.some((l) => l.name === form.proyek) && (
                  <option value={form.proyek}>{form.proyek} (lama)</option>
                )}
              </Select>
            </Field>
            <Field label="No. Truk / Sopir">
              <div className="flex gap-2">
                <Select value={form.no_truk} onChange={(e) => setForm({ ...form, no_truk: e.target.value })} data-testid="material-sj-truck">
                  <option value="">— Pilih Truk —</option>
                  {trucks.map((t) => <option key={t.id} value={t.plate}>{t.plate} {t.capacity_m3 ? `— ${t.capacity_m3} m³` : ""}</option>)}
                </Select>
                <Select value={form.sopir} onChange={(e) => setForm({ ...form, sopir: e.target.value })} data-testid="material-sj-sopir">
                  <option value="">— Pilih Sopir —</option>
                  {drivers.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                </Select>
              </div>
            </Field>
          </div>

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-stone-500 mb-2">Item Material</div>
            <div className="space-y-2">
              {form.items.map((it, i) => {
                const isKg = (it.mode || "kubikasi") === "kilogram";
                return (
                <div key={i} className="border border-stone-200 rounded-lg p-3 bg-stone-50/40 space-y-2" data-testid={`material-sj-item-${i}`}>
                  {/* Mode toggle */}
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Mode:</span>
                    <button
                      type="button"
                      onClick={() => setItem(i, "mode", "kubikasi")}
                      className={`text-xs px-3 py-1 rounded border ${!isKg ? "bg-blue-600 text-white border-blue-700" : "bg-white text-stone-700 border-stone-300 hover:border-stone-400"}`}
                      data-testid={`material-sj-mode-kubikasi-${i}`}
                    >Kubikasi (m³)</button>
                    <button
                      type="button"
                      onClick={() => setItem(i, "mode", "kilogram")}
                      className={`text-xs px-3 py-1 rounded border ${isKg ? "bg-amber-600 text-white border-amber-700" : "bg-white text-stone-700 border-stone-300 hover:border-stone-400"}`}
                      data-testid={`material-sj-mode-kilogram-${i}`}
                    >Kilogram (kg)</button>
                    <div className="flex-1"/>
                    <button onClick={() => rmItem(i)} className="text-stone-400 hover:text-red-700" data-testid={`material-sj-item-remove-${i}`}>
                      <Trash2 className="h-4 w-4"/>
                    </button>
                  </div>

                  <div className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-6">
                      <label className="block text-[10px] uppercase tracking-widest text-stone-500 mb-1">Material</label>
                      <Select
                        value={it.material_id}
                        onChange={(e) => {
                          const m = materials.find((mm) => mm.id === e.target.value);
                          setItem(i, "material_id", e.target.value);
                          if (m) setItem(i, "material_name", m.name);
                          else setItem(i, "material_name", "");
                        }}
                        data-testid={`material-sj-item-material-${i}`}
                      >
                        <option value="">— Pilih Material —</option>
                        {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </Select>
                      {/* Iter v34bm — Input manual nama material HANYA untuk role yang punya perm.
                          Fallback juga muncul untuk edit SJ lama yang material_id sudah tidak ada di master. */}
                      {!it.material_id && (canManualInput || (it.material_name && !materials.some((m) => m.name === it.material_name))) && (
                        <Input
                          className="mt-1"
                          placeholder={canManualInput ? "atau ketik nama manual (super admin)" : "material lama"}
                          value={it.material_name}
                          onChange={(e) => canManualInput && setItem(i, "material_name", e.target.value)}
                          readOnly={!canManualInput}
                          data-testid={`material-sj-item-material-manual-${i}`}
                        />
                      )}
                    </div>

                    {isKg ? (
                      <div className="col-span-6">
                        <label className="block text-[10px] uppercase tracking-widest text-stone-500 mb-1">Berat Muatan (kg)</label>
                        <Input
                          type="number" step="1" min="0"
                          value={it.qty_kg}
                          onChange={(e) => setItem(i, "qty_kg", e.target.value)}
                          className="text-right font-mono-num font-semibold"
                          data-testid={`material-sj-item-qtykg-${i}`}
                        />
                        <p className="text-[10px] text-stone-500 mt-1">Input langsung berat total muatan.</p>
                      </div>
                    ) : (
                      <>
                        <div className="col-span-6 grid grid-cols-5 gap-2">
                          <div className="col-span-1">
                            <label className="block text-[10px] uppercase tracking-widest text-stone-500 mb-1">Truk (prefill)</label>
                            <Select value={it.truck_id || ""} onChange={(e) => setItem(i, "truck_id", e.target.value)} data-testid={`material-sj-item-truck-${i}`}>
                              <option value="">—</option>
                              {trucks.map((t) => <option key={t.id} value={t.id}>{t.plate}</option>)}
                            </Select>
                          </div>
                          <div>
                            <label className="block text-[10px] uppercase tracking-widest text-stone-500 mb-1">Panjang (cm)</label>
                            <Input type="number" step="1" min="0" value={it.panjang} onChange={(e) => setItem(i, "panjang", e.target.value)} className="text-right font-mono-num" data-testid={`material-sj-item-panjang-${i}`}/>
                          </div>
                          <div>
                            <label className="block text-[10px] uppercase tracking-widest text-stone-500 mb-1">Lebar (cm)</label>
                            <Input type="number" step="1" min="0" value={it.lebar} onChange={(e) => setItem(i, "lebar", e.target.value)} className="text-right font-mono-num" data-testid={`material-sj-item-lebar-${i}`}/>
                          </div>
                          <div>
                            <label className="block text-[10px] uppercase tracking-widest text-stone-500 mb-1">Tinggi (cm)</label>
                            <Input type="number" step="1" min="0" value={it.tinggi} onChange={(e) => setItem(i, "tinggi", e.target.value)} className="text-right font-mono-num" data-testid={`material-sj-item-tinggi-${i}`}/>
                          </div>
                          <div>
                            <label className="block text-[10px] uppercase tracking-widest text-stone-500 mb-1">Volume (m³)</label>
                            <Input type="number" step="0.01" min="0" value={it.volume_m3} onChange={(e) => setItem(i, "volume_m3", e.target.value)} className="text-right font-mono-num font-semibold" data-testid={`material-sj-item-vol-${i}`}/>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );})}
              <button onClick={addItem} className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900" data-testid="material-sj-add-item">
                <Plus className="h-3.5 w-3.5"/> Tambah Item
              </button>
            </div>
          </div>

          <Field label="Catatan">
            <Textarea rows={2} value={form.catatan_penting} onChange={(e) => setForm({ ...form, catatan_penting: e.target.value })} placeholder="Instruksi bongkar, dsb"/>
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Nama Penerima">
              <BatchingNamePicker source="receivers" value={form.name_penerima} onPick={({ name }) => setForm({ ...form, name_penerima: name })} data-testid="material-sj-name-penerima"/>
            </Field>
            <Field label="Nama Sopir">
              <BatchingNamePicker source="drivers" value={form.name_sopir} onPick={({ name }) => setForm({ ...form, name_sopir: name })} data-testid="material-sj-name-sopir"/>
            </Field>
            <Field label="Nama Pengirim">
              <BatchingNamePicker source="senders" value={form.name_pengirim} onPick={({ name }) => setForm({ ...form, name_pengirim: name })} data-testid="material-sj-name-pengirim"/>
            </Field>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-stone-100">
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={saving} data-testid="material-sj-save-btn">
              {saving ? "Menyimpan..." : (editId ? "Simpan Perubahan" : "Buat SJ")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* PDF viewer */}
      <PdfViewerModal
        open={!!pdfViewerSj}
        onClose={() => setPdfViewerSj(null)}
        pdfUrl={pdfViewerSj ? `${process.env.REACT_APP_BACKEND_URL}/api/batching/material-sj/${pdfViewerSj.id}/pdf` : ""}
        fileName={`SJ-Material-${pdfViewerSj?.sj_number || pdfViewerSj?.id || "doc"}.pdf`}
        title={`Surat Jalan Material ${pdfViewerSj?.sj_number || ""}`}
      />

      {/* Iter v34ct (#3) — Dialog jumlah lembar SJ kosongan */}
      <Modal open={blankOpen} onClose={() => setBlankOpen(false)} title="Cetak Surat Jalan Material Kosongan" size="sm">
        <div className="space-y-4">
          <p className="text-xs text-stone-600">
            Mencetak blanko Surat Jalan Material dengan kop perusahaan dan garis titik-titik kosong
            untuk diisi tangan di lapangan. Tidak memotong stok dan tidak memakai nomor SJ.
          </p>
          <Field label="Jumlah Lembar" hint="Maksimal 50 lembar per cetak">
            <Input type="number" min="1" max="50" value={blankCopies}
              onChange={(e) => setBlankCopies(e.target.value)} className="font-mono-num"
              data-testid="material-sj-blank-copies" />
          </Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setBlankOpen(false)}>Batal</Button>
            <Button onClick={() => { setBlankOpen(false); setBlankPdfOpen(true); }} data-testid="material-sj-blank-print">
              <Printer className="h-3.5 w-3.5" /> Buka PDF
            </Button>
          </div>
        </div>
      </Modal>

      <PdfViewerModal
        open={blankPdfOpen}
        onClose={() => setBlankPdfOpen(false)}
        pdfUrl={blankPdfOpen ? `${process.env.REACT_APP_BACKEND_URL}/api/batching/material-sj/blank-pdf?copies=${Math.max(1, Math.min(Number(blankCopies) || 1, 50))}` : ""}
        fileName={`SJ-Material-Kosongan-${blankCopies}lembar.pdf`}
        title="Surat Jalan Material — Kosongan"
      />

    </div>
  );
}
